import { P2GGrid, P2GIcon, P2GText, SvgAddCircle, SvgTickCircle } from '@parcel2go/core-ui';
import { useAnalytics, useTaxName } from '@parcel2go/npm-p2g-app';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BasketUpsellDefaultTitle,
  UpsellDefaultIcon
} from '~/components/address-details/upsells/upsell-default-content';
import { useBasketItem } from '~/hooks/use-basket-item';
import { useBasketItemDeliveryGuaranteeDate } from '~/hooks/use-delivery-guarantee-date';
import { TranslationNamespaces } from '~/lib/constants';
import { toDisplayCurrency } from '~/lib/prices';
import { quoteProtectionExtrasFilter } from '~/lib/quotes';
import { AvailableExtra, Currency, QuoteExtras } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { BasketUpsellOptions, OnDemandUpsells, Protection, ProtectionPrefix } from '~/lib/types/extra-types';
import { GetUpdatedQuoteProtectionExtras } from '~/lib/utils/extras-utils';

type BasketItemUpsellsProps = {
  currency: Currency;
  options: AvailableExtra[];
  extras: QuoteExtras;
  itemId: string;
};

export default function BasketItemUpsells({ currency, options, extras, itemId }: BasketItemUpsellsProps) {
  const { update, saving } = useBasketItem<QuoteExtras>({
    name: BasketSections.EXTRAS,
    itemId
  });
  const deliveryGuaranteeDate = useBasketItemDeliveryGuaranteeDate(itemId);
  const isExtraSelected = useCallback((key: string) => !!extras?.extras?.find((extra) => extra.key === key), [extras]);
  const { track } = useAnalytics();

  const visibleOptions = useMemo(() => {
    // This is the level of protection the customer chose on the quote page.
    const quotePageBaseProtection = extras.initialExtras?.filter((e) => e.key == Protection.BASE) || [];
    const allProtectionOptions = options
      .filter(quoteProtectionExtrasFilter)
      .sort(
        (a, b) =>
          Math.max(a.metadata?.maxProtection || 0, a.metadata?.protectedAmount || 0) -
          Math.max(b.metadata?.maxProtection || 0, b.metadata?.protectedAmount || 0)
      );

    // If they didn't take base on the quote page, show both base and full protection options.
    // Otherwise only show the full protection option.
    let visibleProtectionOptions = allProtectionOptions
      .filter((x) => quotePageBaseProtection.length === 0 || x.key !== Protection.BASE)
      .sort((a, b) => b.price.net - a.price.net)
      .slice(0, 1) // Set to 1 to only show 'Full', 2 to show 'Base' and 'Full'
      .reverse();

    // If they've selected FULL, hide the base protection to reduce confusion.
    if (visibleProtectionOptions.filter((x) => x.key == Protection.BASE || x.key === Protection.FULL).length === 2) {
      if (isExtraSelected(Protection.FULL)) {
        visibleProtectionOptions = visibleProtectionOptions.filter((x) => x.key === Protection.FULL);
      }
    }

    const nonProtectionOptions = options.filter((x) => BasketUpsellOptions.includes(x.key));
    return nonProtectionOptions.concat(visibleProtectionOptions);
  }, [options, extras, isExtraSelected]);

  // These are the extras which are only quoted on the Validate endpoint.
  // We don't know the cost here, but we'll display that they've been selected.
  const onDemandAvailableExtras = useMemo(
    () =>
      (extras?.extras?.filter((x) => OnDemandUpsells.includes(x.key)) || []).map((option) => ({
        ...option,
        name: option.key,
        price: { gross: 0, net: 0, tax: 0 },
        metadata: {},
        tags: []
      })),
    [extras]
  );

  const onToggleExtra = (key: string) => {
    if (saving) {
      return;
    }

    const extraIsCurrentlySelected = isExtraSelected(key);
    if (key.startsWith(ProtectionPrefix)) {
      update({
        ...extras,
        extras: GetUpdatedQuoteProtectionExtras(key, !extraIsCurrentlySelected, extras.extras, extras.initialExtras),
        userInteractedWithProtection: true
      });
    } else {
      update({
        ...extras,
        extras: extraIsCurrentlySelected
          ? extras.extras.filter((extra) => extra.key !== key)
          : [...extras.extras, { key: key, metadata: {} }]
      });
    }

    const matchingExtra = options.find((x) => x.key === key);
    if (matchingExtra) {
      if (extraIsCurrentlySelected) {
        track({
          type: 'remove_from_cart',
          payload: {
            ecommerce: {
              currency: currency.code,
              value: matchingExtra.price.net / 100,
              items: [
                {
                  item_id: key,
                  item_name: matchingExtra.name,
                  item_brand: 'Parcel2Go',
                  price: matchingExtra.price.net / 100,
                  item_category: 'Extra'
                }
              ]
            }
          }
        });
      } else {
        track({
          type: 'add_to_cart',
          payload: {
            ecommerce: {
              currency: currency.code,
              value: matchingExtra.price.net / 100,
              items: [
                {
                  item_id: key,
                  item_name: matchingExtra.name,
                  item_brand: 'Parcel2Go',
                  price: matchingExtra.price.net / 100,
                  item_category: 'Extra'
                }
              ]
            }
          }
        });
      }
    }
  };

  return (
    <P2GGrid
      dataTestId="basket-item-upsells-container"
      container
      direction="column"
      gap={1}
      sx={{ opacity: saving ? '0.3' : 1 }}
    >
      {visibleOptions.map((option) => (
        <BasketUpsell
          currency={currency}
          deliveryGuaranteeDate={deliveryGuaranteeDate}
          key={option.key}
          onToggle={() => onToggleExtra(option.key)}
          option={option}
          selected={isExtraSelected(option.key)}
        />
      ))}
      {onDemandAvailableExtras.map((option) => (
        <BasketUpsell
          currency={currency}
          deliveryGuaranteeDate={deliveryGuaranteeDate}
          key={option.key}
          option={option}
          selected={isExtraSelected(option.key)}
        />
      ))}
    </P2GGrid>
  );
}

const BasketUpsell = ({
  option,
  currency,
  selected,
  deliveryGuaranteeDate,
  onToggle
}: {
  option: AvailableExtra;
  currency: Currency;
  selected: boolean;
  deliveryGuaranteeDate?: Date | null;
  onToggle?: () => void;
}) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const taxName = useTaxName();
  const IconComponent = UpsellDefaultIcon(option.key, selected);

  return (
    <P2GGrid item key={option.key}>
      <P2GGrid container spacing={1} alignItems="center" wrap="nowrap">
        <P2GGrid item>
          <P2GIcon size="xs" palette="primary">
            <IconComponent />
          </P2GIcon>
        </P2GGrid>
        <P2GGrid item flexGrow={1}>
          <P2GText variant="body2" fontWeight="bold">
            {t(
              (selected ? `UPSELL_LIST_${option.key}_SELECTED_TITLE` : `UPSELL_LIST_${option.key}_TITLE`).toUpperCase(),
              {
                extra: option,
                deliveryGuaranteeDate: deliveryGuaranteeDate?.toDateString(),
                price: toDisplayCurrency(currency, option.price?.net),
                protectionAmount: toDisplayCurrency(currency, option.metadata?.protectedAmount || 0, 0),
                taxName: taxName,
                defaultValue: BasketUpsellDefaultTitle(
                  option.key,
                  option.price?.net || 0,
                  selected,
                  option.price?.tax || 0
                )
              }
            )}
          </P2GText>
        </P2GGrid>

        <P2GGrid item>
          {selected ? (
            <P2GIcon size="xs" palette="primary" onClick={onToggle}>
              <SvgTickCircle />
            </P2GIcon>
          ) : (
            <P2GIcon size="xs" palette="success" onClick={onToggle}>
              <SvgAddCircle />
            </P2GIcon>
          )}
        </P2GGrid>
      </P2GGrid>
    </P2GGrid>
  );
};
